import { makeAutoObservable, runInAction } from "mobx";
import { showToast } from "@/lib/utils/toast";
import { t } from "i18next";
import { responseSurveyService } from "../services/response-survey.service";

const DEFAULT_FILTERS = {
  search: "",
  surveyId: "all",
  site: "all",
  status: "all",
  priorityLabel: "all",
  sort: "recently_submitted",
};

const DEFAULT_PAGINATION = {
  page: 1,
  limit: 20,
  total: 0,
  pages: 1,
};

const DEFAULT_STATS = {
  total: 0,
  newCount: 0,
  surveyCount: 0,
};

const buildQueryParams = (filters, pagination) => {
  const params = {
    page: pagination.page,
    limit: pagination.limit,
    sort: filters.sort,
  };
  if (filters.search.trim()) params.search = filters.search.trim();
  if (filters.surveyId !== "all") params.surveyId = filters.surveyId;
  if (filters.status !== "all") params.status = filters.status;
  if (filters.priorityLabel !== "all")
    params.priorityLabel = filters.priorityLabel;
  if (filters.site !== "all") params.site = filters.site;
  return params;
};

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────
class ResponseSurveyStore {
  responses = [];
  selected = null;
  stats = { ...DEFAULT_STATS };
  filters = { ...DEFAULT_FILTERS };
  pagination = { ...DEFAULT_PAGINATION };
  submitting = false;
  loading = false;
  initializing = true; // ✅ added
  error = null;
  isUploading = false;

  constructor() {
    makeAutoObservable(this);
  }

  // ─── COMPUTED ─────────────────────────────────────────────────────────
  get canGoPrev() {
    return this.pagination.page > 1;
  }
  get canGoNext() {
    return this.pagination.page < this.pagination.pages;
  }

  get paginationInfo() {
    const { page, limit, total } = this.pagination;
    if (total === 0) return { start: 0, end: 0, total: 0, pages: 1 };
    return {
      start: (page - 1) * limit + 1,
      end: Math.min(page * limit, total),
      total,
      pages: this.pagination.pages,
    };
  }

  // ─── FILTER SETTERS ───────────────────────────────────────────────────
  setSearch = (v) => {
    this.filters.search = v;
    this.pagination.page = 1;
  };
  setSurveyFilter = (v) => {
    this.filters.surveyId = v;
    this.pagination.page = 1;
  };
  setSiteFilter = (v) => {
    this.filters.site = v;
    this.pagination.page = 1;
  };
  setStatus = (v) => {
    this.filters.status = v;
    this.pagination.page = 1;
  };
  setPriorityLabel = (v) => {
    this.filters.priorityLabel = v;
    this.pagination.page = 1;
  };
  setSort = (v) => {
    this.filters.sort = v;
    this.pagination.page = 1;
  };

  // ─── PAGINATION ───────────────────────────────────────────────────────
  setPage = (page) => {
    this.pagination.page = page;
  };
  setRowsPerPage = (limit) => {
    this.pagination.limit = limit;
    this.pagination.page = 1;
  };
  goNextPage = () => {
    if (this.canGoNext) this.pagination.page += 1;
  };
  goPrevPage = () => {
    if (this.canGoPrev) this.pagination.page -= 1;
  };

  // ─── INITIALIZE ───────────────────────────────────────────────────────
  initialize = async () => {
    runInAction(() => {
      this.initializing = true;
    });
    await this.fetchResponses();
    runInAction(() => {
      this.initializing = false;
    });
  };

  clearResponses = () => {
    runInAction(() => {
      this.responses = [];
      this.selected = null;
      this.error = null;
    });
  };

  // ─── FETCH LIST ───────────────────────────────────────────────────────
  fetchResponses = async () => {
    runInAction(() => {
      this.loading = true;
    });
    try {
      const params = buildQueryParams(this.filters, this.pagination);
      const response = await responseSurveyService.listSurveyResponses(params);
      const data = response?.data || response;
      runInAction(() => {
        this.responses = data?.result ?? [];
        this.pagination = {
          ...this.pagination,
          total: data?.pagination?.total ?? 0,
          pages: data?.pagination?.totalPages ?? 1,
        };
        if (data?.stats) this.stats = data.stats;
      });
    } catch (err) {
      const message =
        err?.response?.data?.message ??
        err?.message ??
        t("Failed to fetch responses");
      showToast.error(message);
      runInAction(() => {
        this.responses = [];
        this.error = message;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
        this.initializing = false;
      });
    }
  };

  // ─── SUBMIT (public take-survey) ──────────────────────────────────────
  submitResponse = async (payload) => {
    runInAction(() => {
      this.submitting = true;
      this.error = null;
    });
    try {
      const response = await responseSurveyService.submitResponse(payload);
      const data = response?.data || response;
      return data?.result || data;
    } catch (err) {
      const message =
        err?.response?.data?.message ?? err?.message ?? t("Submission failed");
      runInAction(() => {
        this.error = message;
      });
      showToast.error(message);
      throw err;
    } finally {
      runInAction(() => {
        this.submitting = false;
      });
    }
  };

  // ─── FETCH BY ID ──────────────────────────────────────────────────────
  fetchResponseById = async (id) => {
    runInAction(() => {
      this.loading = true;
      this.error = null;
      this.selected = null;
    });
    try {
      const params = buildQueryParams(this.filters, this.pagination);
      const response = await responseSurveyService.getSurveyResponseById(
        id,
        params,
      );
      const data = response?.data || response;
      const result = data?.result || data;
      runInAction(() => {
        this.selected = result;
      });
      return result;
    } catch (err) {
      const message =
        err?.response?.data?.message ??
        err?.message ??
        t("Failed to fetch response");
      runInAction(() => {
        this.error = message;
      });
      showToast.error(message);
      throw err;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  // ─── UPDATE ───────────────────────────────────────────────────────────
  updateResponse = async (id, payload) => {
    runInAction(() => {
      this.submitting = true;
    });
    try {
      const response = await responseSurveyService.updateSurveyResponse(
        id,
        payload,
      );
      const data = response?.data || response;
      const updated = data?.result || data;
      showToast.success(t("Responses updated successfully."));
      await this.fetchResponses();
      return updated;
    } catch (err) {
      showToast.error(err?.message || t("Failed to update response."));
      throw err;
    } finally {
      runInAction(() => {
        this.submitting = false;
      });
    }
  };
  uploadAttachment = async (companyId, files) => {
    runInAction(() => {
      this.isUploading = true;
    });
    if (!companyId) return [];

    try {
      const response = await responseSurveyService.uploadAttachment(
        companyId,
        files,
      );
      const result = response?.data ?? response;

      const mapped = result.map((f) => ({
        fileId: f._id,
        name: f.originalName,
        size: f.size,
        type: f.mimeType,
        mimeType: f.mimeType,
        url: f.url,
        uploadedAt: f.createdAt,
        companyId: f.companyId,
        userId: f.userId,
      }));

      return mapped; // ← no toast here, UI shows per-file status icons
    } catch (err) {
      showToast.error("Failed to upload files");
      return [];
    } finally {
      runInAction(() => {
        this.isUploading = false;
      });
    }
  };
  deleteAttachment = async (fileId) => {
    try {
      await responseSurveyService.deleteAttachment(fileId);
      showToast.success("Successfully deleted!");
    } catch (error) {
      showToast.error(error?.message || "Failed to delete!");
      throw error;
    }
  };
  downloadAttachment = async (proof) => {
    const fileId = proof.fileId;
    if (!fileId) return;
    const downloadPromise = responseSurveyService.downloadAttachment(proof);
    showToast.promise(downloadPromise, {
      loading: "Downloading",
      success: "Successfully downloaded",
      error: "Failed to download",
    });
    try {
      await promiseWithMinDelay(downloadPromise);
    } catch {
      // showToast.promise already handled the error toast — swallow silently
    }
  };
}

export const responseSurveyStore = new ResponseSurveyStore();
